import {
  useGetTenderMaterialsQuery,
  useGetTenderRepliesQuery,
  useGetTenderReplyItemsMutation,
  useGetTenderVendorContactsMutation,
} from '@Features/MaterialRequest/requests/TenderRequest/store/api/TenderRequestApiSlice';
import { useCallback, useEffect, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import { materialRequestActions } from '@Features/MaterialRequest/requests/MaterialRequisition/store/materialRequestSlice';
import { useAppDispatch } from '@Hooks/index';
import { TenderReplyItemsDTO } from '@Types/api/form/tenderRequest/TenderReplyItemsDTO';
import {
  PriceComparisonDto,
  TenderSupplier,
  VendorOfferDto,
} from '../ComparisonSheet.types';

const mapRepliesToOfferData =
  (vendorId: number) =>
  (reply: TenderReplyItemsDTO): VendorOfferDto => {
    const {
      rate,
      isStockAvailable,
      availableBrands,
      tenderItemId,
      itemRec,
      freeItem,
      vendorQty,
    } = reply;
    return {
      rate: Number(rate),
      amount: vendorQty,
      available: isStockAvailable,
      brand: availableBrands,
      vendorId,
      tenderItemId,
      itemRec,
      vendorQty,
      freeItem,
    };
  };

export const usePrepareComparisonSheetData = (): {
  loading: boolean;
} => {
  const dispatch = useAppDispatch();
  const { tenderId } = useParams();
  const [getVendorContacts] = useGetTenderVendorContactsMutation();
  const [getReplyItems] = useGetTenderReplyItemsMutation();

  const { data: materials, isLoading: materialsLoading } =
    useGetTenderMaterialsQuery(tenderId || '', {
      skip: !tenderId,
    });

  const {
    data: replies,
    isLoading: repliesLoading,
    isFetching,
  } = useGetTenderRepliesQuery(tenderId || '', {
    skip: !tenderId,
  });

  const getOffersFromReplyItems = useCallback(
    async (
      replyId: string,
      vendorId: number
    ): Promise<Array<VendorOfferDto>> => {
      const response = await getReplyItems(replyId).unwrap();
      const offers = response.map(mapRepliesToOfferData(vendorId));
      return offers;
    },
    [getReplyItems]
  );

  const getSupplierFromVendor = useCallback(
    async (vendorId: string): Promise<TenderSupplier> => {
      const contacts = await getVendorContacts(vendorId).unwrap();
      const vendorName =
        contacts?.find((contact) => contact.occupation === '')?.name || '';
      return { id: Number(vendorId), name: vendorName };
    },
    [getVendorContacts]
  );

  useLayoutEffect(() => {
    if (replies?.length && !isFetching) {
      const replyPromises: Array<Promise<VendorOfferDto[]>> = [];
      const vendorPromises: Array<Promise<TenderSupplier>> = [];
      replies.forEach(async (reply) => {
        replyPromises.push(
          getOffersFromReplyItems(String(reply.replyId), reply.vendorId)
        );
        vendorPromises.push(getSupplierFromVendor(String(reply.vendorId)));
      });
      Promise.all(replyPromises).then((records) => {
        const allTenderReplies = records.flat();
        const comparsionSheetRecords: PriceComparisonDto[] | undefined =
          materials?.map((material) => ({
            matId: material.matId,
            material: {
              code: material.materialCode,
              description: material.materialDesc,
              unit: material.unitName,
              requiredQty: material.reqQty,
              matId: material.matId,
            },
            vendorOffers: allTenderReplies.filter(
              (rec) => Number(rec.tenderItemId) === material.tenderItemID
            ),
          }));
        if (comparsionSheetRecords) {
          dispatch(
            materialRequestActions.setComparisonSheet(comparsionSheetRecords)
          );
        }
      });
      Promise.all(vendorPromises).then((materialSuppliers) => {
        if (materialSuppliers) {
          dispatch(
            materialRequestActions.setMaterialSuppliers(materialSuppliers)
          );
        }
      });
      Promise.all([replyPromises, vendorPromises]);
    }
  }, [
    dispatch,
    getOffersFromReplyItems,
    getSupplierFromVendor,
    isFetching,
    materials,
    replies,
  ]);

  // clear state on unmount
  useEffect(() => {
    return () => {
      materialRequestActions.setMaterialSuppliers([]);
      materialRequestActions.setComparisonSheet([]);
    };
  }, []);

  return { loading: materialsLoading || repliesLoading };
};
